import React, { useEffect, useState } from "react";
import {
  Popconfirm,
  Input,
  Flex,
  Tooltip,
  Tag,
  Modal,
  Form,
  Select,
  DatePicker,
  message,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import fileDownload from "js-file-download";
import { useNavigate } from "react-router-dom";
import useUrlFilters from "hooks/useUrlFilters";
import * as actions from "store/actions/AuthActions";
import dayjs from "dayjs";
import FilterBar from "components/Common/FilterBar";

const { TextArea } = Input;
const { Option } = Select;

// Search and paging live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
  search: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 100 },
};

const ContractsTable = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const [form] = Form.useForm();

  const searchTerm = filters.search || "";
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Backend pagination state
  const currentPage = filters.page;
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state (for creating new contracts only)
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Contract types for dropdown
  const [contractTypes, setContractTypes] = useState([]);

  // Employees and accounts for counterparty dropdowns
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const fetchContracts = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadContracts(page, searchTerm, size);
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error("Error fetching contracts:", e);
    }
    setLoading(false);
  };

  const fetchContractTypesForDropdown = async () => {
    try {
      const response = await actions.loadContractTypes(1, "", 1000);
      if (response.status === 200) {
        setContractTypes(response.data.results || []);
      }
    } catch (e) {
      console.error("Error fetching contract types:", e);
    }
  };

  const fetchEmployeesForDropdown = async () => {
    try {
      const response = await actions.loadAllEmployees("");
      if (response.status === 200) {
        setEmployees(response.data.results || []);
      }
    } catch (e) {
      console.error("Error fetching employees:", e);
    }
  };

  const fetchAccountsForDropdown = async () => {
    try {
      const response = await actions.loadAllAccounts("");
      if (response.status === 200) {
        setAccounts(response.data.results || []);
      }
    } catch (e) {
      console.error("Error fetching accounts:", e);
    }
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setFilters({ search: value, page: 1 });
    }
  };

  // Fetch contracts when search term or pagination changes
  useEffect(() => {
    fetchContracts(currentPage, pageSize);
  }, [searchTerm, currentPage, pageSize]);

  // Fetch dropdown data on mount
  useEffect(() => {
    fetchContractTypesForDropdown();
    fetchEmployeesForDropdown();
    fetchAccountsForDropdown();
  }, []);

  const handleDelete = async (id) => {
    const response = await actions.deleteContract(id);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchContracts(currentPage, pageSize);
    }
  };

  const handleDownloadPdf = async (record, fileName) => {
    const response = await actions.downloadContractPdf(record.id);
    fileDownload(response.data, fileName || record.pdf_document_data?.name || `${record.title}.pdf`);
  };

  // Export (re)generates the contract's PDF, then downloads it.
  const handleExportPdf = async (record) => {
    try {
      message.loading({ content: 'Generating PDF...', key: 'exportPdf' });
      const response = await actions.exportContractToPdf(record.id);
      await handleDownloadPdf(record, response.data.file_name);
      message.success({ content: 'PDF generated and downloaded', key: 'exportPdf' });
      fetchContracts(currentPage, pageSize);
    } catch (error) {
      const reason = error?.response?.data?.error;
      message.error({ content: reason || 'Failed to export PDF', key: 'exportPdf' });
    }
  };

  // Modal handlers (for creating new contracts only)
  const handleCreateNew = () => {
    form.resetFields();
    form.setFieldsValue({ status: 'DRAFT', contract_term: 'DEFINITE' });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Get html_code from selected contract type
      const selectedContractType = contractTypes.find(t => t.id === values.contract_type);

      // Convert dayjs objects to date strings and include html_content
      const submitData = {
        ...values,
        contract_effective_date: values.contract_effective_date?.format('YYYY-MM-DD') || null,
        contract_end_date: values.contract_end_date?.format('YYYY-MM-DD') || null,
        // Include html_code from selected contract type as html_content
        html_content: selectedContractType?.html_code || '',
      };

      const response = await actions.addContract(submitData);
      message.success('Contract created successfully');
      setIsModalVisible(false);
      form.resetFields();
      // Navigate to editor page with new contract ID
      navigate(`/contract/edit/${response.data.id}`);
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error('Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
    },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "DRAFT":
        return "orange";
      case "EXPIRED":
        return "red";
      case "TERMINATED":
        return "gray";
      default:
        return "default";
    }
  };

  const defaultColumns = [
    {
      title: "TITLE",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a?.title || "").localeCompare(b?.title || ""),
      width: 250,
      fixed: "left",
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "CONTRACT TYPE",
      dataIndex: "contract_type_data",
      key: "contract_type",
      width: 200,
      render: (contractType) => contractType?.name || "-",
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "CREATED DATE",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        new Date(a?.created_at) - new Date(b?.created_at),
      width: 180,
      render: (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      },
    },
    {
      title: "Actions",
      dataIndex: "edit",
      key: "edit",
      width: 180,
      render: (_, record) => {
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <Tooltip title="Edit">
              <a>
                <EditOutlined
                  onClick={() => navigate(`/contract/edit/${record.id}`)}
                  style={{
                    verticalAlign: "middle",
                  }}
                />
              </a>
            </Tooltip>
            {record.pdf_document_data && (
              <Tooltip title={`Download last PDF (${dayjs(record.pdf_document_data.updated_at).format("D MMM YYYY, h:mm a")})`}>
                <a onClick={() => handleDownloadPdf(record).catch(() => message.error("Could not download the PDF"))}>
                  <DownloadOutlined style={{ verticalAlign: "middle" }} />
                </a>
              </Tooltip>
            )}
            <Tooltip title={record.pdf_document_data ? "Regenerate PDF" : "Export to PDF"}>
              <a onClick={() => handleExportPdf(record)}>
                <FilePdfOutlined
                  style={{
                    verticalAlign: "middle",
                    color: "#cf1322",
                  }}
                />
              </a>
            </Tooltip>
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null;
      },
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      align: "center",
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    };
  });

  const [selectionType] = useState("checkbox");

  // Handle pagination change
  const handleTableChange = (page, size) => {
    // Back to the first page when the page size changes.
    setFilters({ page: size !== pageSize ? 1 : page, pageSize: size });
  };

  return (
    <div style={{ padding: 10 }}>
      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
        {Math.min(currentPage * pageSize, totalRows)} of {totalRows}{" "}
        contracts
        </div>
      <Flex
        gap="large"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    {/* Contracts filter on search alone, so FilterBar renders just the
              search box — it hides the Filter button when it has no children. */}
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search by title or counterparty"
            onRefresh={() => fetchContracts(currentPage, pageSize)}
            refreshing={loading}
            addLabel="Add Contract"
            addIcon={<PlusOutlined />}
            onAdd={handleCreateNew}
/>
        </div>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={(row) => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ["50", "100", "200"],
            onChange: handleTableChange,
          }}
          scroll={{
            x: "max-content",
            y: "calc(100vh - 350px)",
          }}
          sticky
        />
      </div>

      {/* Create Contract Modal */}
      <Modal
        title="Create Contract"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={submitting}
        width={900}
      >
        <Form form={form} layout="vertical">
          {/* Basic Info */}
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter contract title' }]}
          >
            <Input placeholder="Enter contract title" />
          </Form.Item>

          <Flex gap="middle">
            <Form.Item
              name="contract_type"
              label="Contract Type"
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select contract type"
                allowClear
                onChange={(value) => {
                  if (value) {
                    const selectedType = contractTypes.find(t => t.id === value);
                    if (selectedType?.html_code) {
                      form.setFieldsValue({ html_content: selectedType.html_code });
                    }
                  } else {
                    form.setFieldsValue({ html_content: '' });
                  }
                }}
              >
                {contractTypes.map(type => (
                  <Option key={type.id} value={type.id}>{type.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              style={{ flex: 1 }}
            >
              <Select placeholder="Select status">
                <Option value="DRAFT">Draft</Option>
                <Option value="ACTIVE">Active</Option>
                <Option value="EXPIRED">Expired</Option>
                <Option value="TERMINATED">Terminated</Option>
              </Select>
            </Form.Item>
          </Flex>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter description (optional)" />
          </Form.Item>

          {/* Requester Info */}
          <Flex gap="middle">
            <Form.Item name="requester_name" label="Requester Name" style={{ flex: 1 }}>
              <Input placeholder="Enter requester name" />
            </Form.Item>
            <Form.Item name="requester_department" label="Requester Department" style={{ flex: 1 }}>
              <Input placeholder="Enter requester department" />
            </Form.Item>
          </Flex>

          {/* Counterparty Info */}
          <Form.Item
            name="counterparty_name"
            label="Counterparty Name"
            rules={[{ required: true, message: 'Please enter counterparty name' }]}
          >
            <Input placeholder="Enter counterparty name" />
          </Form.Item>

          <Flex gap="middle">
            <Form.Item name="counterparty_contact" label="Counterparty Contact" style={{ flex: 1 }}>
              <Input placeholder="Enter contact info" />
            </Form.Item>
            <Form.Item name="counterparty_primary_contact" label="Primary Contact" style={{ flex: 1 }}>
              <Input placeholder="Enter primary contact" />
            </Form.Item>
          </Flex>

          {/* Counterparty Employee */}
          <Flex gap="middle">
            <Form.Item name="counterparty_employee" label="Counterparty Employee" style={{ flex: 1 }}>
              <Select
                placeholder="Select employee"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={(value) => {
                  if (value) {
                    const selectedEmployee = employees.find(e => e.id === value);
                    // Priority: personal_email > user_email > empty
                    const email = selectedEmployee?.personal_email || selectedEmployee?.user_email || '';
                    form.setFieldsValue({ counterparty_employee_email: email });
                  } else {
                    form.setFieldsValue({ counterparty_employee_email: '' });
                  }
                }}
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.user_name} ({emp.employee_id})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="counterparty_employee_email" label="Employee Email" style={{ flex: 1 }}>
              <Input placeholder="Auto-filled from selected employee" />
            </Form.Item>
          </Flex>

          {/* Counterparty Account */}
          <Flex gap="middle">
            <Form.Item name="counterparty_account" label="Counterparty Account" style={{ flex: 1 }}>
              <Select
                placeholder="Select account"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={(value) => {
                  if (value) {
                    const selectedAccount = accounts.find(a => a.id === value);
                    form.setFieldsValue({
                      counterparty_account_email: selectedAccount?.email || ''
                    });
                  } else {
                    form.setFieldsValue({ counterparty_account_email: '' });
                  }
                }}
              >
                {accounts.map(acc => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.name} ({acc.account_number})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="counterparty_account_email" label="Account Email" style={{ flex: 1 }}>
              <Input placeholder="Auto-filled from selected account" />
            </Form.Item>
          </Flex>

          {/* Contract Dates */}
          <Flex gap="middle">
            <Form.Item name="contract_effective_date" label="Effective Date" style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="contract_end_date"
              label="End Date"
              style={{ flex: 1 }}
              dependencies={['contract_effective_date']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const effectiveDate = getFieldValue('contract_effective_date');
                    if (!value || !effectiveDate) {
                      return Promise.resolve();
                    }
                    if (value.isBefore(effectiveDate, 'day')) {
                      return Promise.reject(new Error('End date must be same or after effective date'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Flex>

          {/* Contract Terms */}
          <Flex gap="middle">
            <Form.Item name="contract_term" label="Contract Term" style={{ flex: 1 }}>
              <Select placeholder="Select contract term">
                <Option value="DEFINITE">Definite</Option>
                <Option value="INDEFINITE">Indefinite</Option>
              </Select>
            </Form.Item>
            <Form.Item name="termination_notice_period" label="Termination Notice Period" style={{ flex: 1 }}>
              <Input placeholder="e.g., 30 days" />
            </Form.Item>
          </Flex>
        </Form>
      </Modal>
    </div>
  );
};

export default ContractsTable;
